import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Euro, CalendarRange, Wallet, AlertTriangle } from "lucide-react";
import { Panel } from "@/components/core/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { eur, MESI } from "@/lib/invoices";

const Card = ({ icon: Icon, label, value, sub, danger, testid }) => (
  <Panel className="p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-600"><Icon className={`h-4 w-4 ${danger ? "text-[#E8173A]" : "text-[#0A1128]"}`} />{label}</div>
    <div className={`font-display text-3xl font-black mt-2 ${danger ? "text-[#C8102E]" : "text-[#0A1128]"}`} data-testid={`${testid}-value`}>{value}</div>
    {sub && <div className="text-xs text-slate-600 mt-1">{sub}</div>}
  </Panel>
);

export const InvoiceSummary = ({ s, compact }) => {
  const { user } = useAuth();
  if (!s) return null;
  const now = new Date();
  return (
    <div className="space-y-4 mb-6" data-testid="invoice-summary">
      <div className={`grid gap-3 sm:gap-4 core-m2 ${compact ? "grid-cols-2 md:grid-cols-3" : "grid-cols-2 lg:grid-cols-4"}`}>
        {!compact && <Card icon={Euro} label={`Fatturato ${MESI[now.getMonth()].toLowerCase()}`} value={eur(s.fatturato_mese)} testid="inv-card-mese" />}
        {compact ? <Card icon={CalendarRange} label={`Fatturato ${s.anno}`} value={eur(s.fatturato_anno)} testid="inv-card-anno" />
          : <Card icon={CalendarRange} label={`Fatturato ${now.getFullYear()}`} value={eur(s.fatturato_anno_corrente)} testid="inv-card-anno" />}
        <Card icon={Wallet} label="Da incassare" value={eur(s.da_incassare)} testid="inv-card-incassare" />
        <Card icon={AlertTriangle} label="Scadute" value={eur(s.scadute_totale)} sub={`${s.scadute_count} fatture`} danger={s.scadute_count > 0} testid="inv-card-scadute" />
      </div>
      {!compact && (
        <div className={`grid gap-4 ${user.role === "admin" && s.per_socio ? "grid-cols-[1.6fr_1fr]" : "grid-cols-1"}`}>
          <Panel className="p-5" data-testid="inv-chart">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">Fatturato mensile {s.anno}</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={s.mensile.map((m) => ({ ...m, nome: MESI[m.mese - 1].slice(0, 3) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#5FB3EA" vertical={false} />
                <XAxis dataKey="nome" tick={{ fill: "#0A1128", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#0A1128", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                <Tooltip formatter={(v) => eur(v)} cursor={{ fill: "#A6DAFC" }} />
                <Bar dataKey="totale" name="Fatturato" fill="#E8173A" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
          {user.role === "admin" && s.per_socio && (
            <Panel className="p-5" data-testid="inv-per-socio">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">Per socio · {s.anno}</div>
              <table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-600"><th className="pb-2">Socio</th><th className="pb-2 text-right">Fatturato</th><th className="pb-2 text-right">Da incassare</th><th className="pb-2 text-right">Scadute</th></tr></thead>
                <tbody>{s.per_socio.map((p) => (
                  <tr key={p.socio_id} className="border-t border-[#5FB3EA]/60" data-testid={`inv-socio-${p.socio_id}`}><td className="py-2 font-bold text-[#0A1128]">{p.socio_nome}</td>
                    <td className="py-2 text-right">{eur(p.fatturato_anno)}</td><td className="py-2 text-right">{eur(p.da_incassare)}</td><td className="py-2 text-right">{p.scadute_count}</td></tr>
                ))}{!s.per_socio.length && <tr><td colSpan={4} className="py-3 text-slate-600">Nessuna fattura.</td></tr>}</tbody></table>
            </Panel>
          )}
        </div>
      )}
    </div>
  );
};
